import type { InjectionKey, PropType, Ref } from 'vue'
import { computed, defineComponent, h, inject, provide, ref } from 'vue'
import { cn } from '@/lib/utils'

type SidebarState = {
  open: Ref<boolean>
  state: Ref<'expanded' | 'collapsed'>
  setOpen: (value: boolean) => void
  toggleSidebar: () => void
}

const SIDEBAR_CONTEXT_KEY: InjectionKey<SidebarState> = Symbol('SidebarContext')

const createPrimitive = (name: string, baseClass: string, tag = 'div') =>
  defineComponent({
    name,
    props: {
      class: {
        type: null as unknown as PropType<string | undefined>,
        default: undefined,
      },
    },
    setup(props, { slots, attrs }) {
      return () =>
        h(
          tag,
          {
            ...attrs,
            class: cn(baseClass, props.class),
          },
          slots.default?.(),
        )
    },
  })

export const SidebarProvider = defineComponent({
  name: 'SidebarProvider',
  props: {
    open: {
      type: Boolean as PropType<boolean | undefined>,
      default: undefined,
    },
    defaultOpen: {
      type: Boolean,
      default: true,
    },
    class: {
      type: null as unknown as PropType<string | undefined>,
      default: undefined,
    },
  },
  emits: ['update:open'],
  setup(props, { slots, emit }) {
    const uncontrolledOpen = ref(props.defaultOpen)
    const open = computed({
      get: () => props.open ?? uncontrolledOpen.value,
      set: (value: boolean) => {
        uncontrolledOpen.value = value
        emit('update:open', value)
      },
    })
    const state = computed<'expanded' | 'collapsed'>(() =>
      open.value ? 'expanded' : 'collapsed',
    )
    const setOpen = (value: boolean) => {
      open.value = value
    }
    const toggleSidebar = () => {
      setOpen(!open.value)
    }

    provide(SIDEBAR_CONTEXT_KEY, {
      open,
      state,
      setOpen,
      toggleSidebar,
    })

    return () =>
      h(
        'div',
        {
          'data-slot': 'sidebar-provider',
          'data-state': state.value,
          class: cn(
            'group/sidebar-wrapper flex h-full min-h-0 w-full text-sidebar-foreground',
            props.class,
          ),
        },
        slots.default?.(),
      )
  },
})

export const useSidebar = () => {
  const context = inject(SIDEBAR_CONTEXT_KEY)
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider.')
  }
  return context
}

export const Sidebar = defineComponent({
  name: 'Sidebar',
  props: {
    side: {
      type: String as PropType<'left' | 'right'>,
      default: 'left',
    },
    variant: {
      type: String as PropType<'sidebar' | 'floating' | 'inset'>,
      default: 'sidebar',
    },
    collapsible: {
      type: String as PropType<'offcanvas' | 'icon' | 'none'>,
      default: 'icon',
    },
    class: {
      type: null as unknown as PropType<string | undefined>,
      default: undefined,
    },
  },
  setup(props, { slots, attrs }) {
    const context = inject(SIDEBAR_CONTEXT_KEY, null)
    const state = computed(() => context?.state.value ?? 'expanded')

    return () =>
      h(
        'aside',
        {
          ...attrs,
          'data-slot': 'sidebar',
          'data-state': state.value,
          'data-side': props.side,
          'data-variant': props.variant,
          'data-collapsible': props.collapsible,
          class: cn(
            'flex h-full min-h-0 w-full flex-col bg-sidebar text-sidebar-foreground',
            props.variant === 'floating' && 'rounded-lg border border-sidebar-border shadow-sm',
            props.class,
          ),
        },
        slots.default?.(),
      )
  },
})

export const SidebarHeader = createPrimitive(
  'SidebarHeader',
  'flex shrink-0 flex-col gap-2 p-2',
  'header',
)
export const SidebarContent = createPrimitive(
  'SidebarContent',
  'flex min-h-0 flex-1 flex-col gap-2 overflow-hidden',
  'div',
)
export const SidebarFooter = createPrimitive(
  'SidebarFooter',
  'flex shrink-0 flex-col gap-2 p-2',
  'footer',
)
export const SidebarGroup = createPrimitive('SidebarGroup', 'relative flex w-full flex-col p-0')
export const SidebarGroupLabel = createPrimitive(
  'SidebarGroupLabel',
  'flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-semibold text-sidebar-foreground/70 outline-none',
)
export const SidebarGroupContent = createPrimitive(
  'SidebarGroupContent',
  'w-full text-sm',
)
export const SidebarMenu = createPrimitive(
  'SidebarMenu',
  'm-0 flex w-full min-w-0 list-none flex-col gap-1 p-0',
  'ul',
)
export const SidebarMenuItem = createPrimitive('SidebarMenuItem', 'relative m-0 min-w-0 p-0', 'li')
export const SidebarMenuBadge = createPrimitive(
  'SidebarMenuBadge',
  'pointer-events-none absolute right-1 top-1/2 flex h-5 min-w-5 -translate-y-1/2 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground/70',
  'span',
)

export const SidebarMenuButton = defineComponent({
  name: 'SidebarMenuButton',
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as PropType<'sm' | 'default' | 'lg'>,
      default: 'default',
    },
    class: {
      type: null as unknown as PropType<string | undefined>,
      default: undefined,
    },
  },
  setup(props, { slots, attrs }) {
    return () =>
      h(
        'button',
        {
          type: 'button',
          ...attrs,
          'data-slot': 'sidebar-menu-button',
          'data-active': props.isActive ? 'true' : undefined,
          class: cn(
            'peer/menu-button flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-md px-2 text-left text-sm outline-none transition-[background-color,color,box-shadow] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring disabled:pointer-events-none disabled:opacity-50 data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground data-active:font-medium [&>svg]:size-4 [&>svg]:shrink-0',
            props.size === 'sm' && 'h-7 text-xs',
            props.size === 'default' && 'h-8',
            props.size === 'lg' && 'h-12',
            props.class,
          ),
        },
        slots.default?.(),
      )
  },
})

export const SidebarSeparator = createPrimitive(
  'SidebarSeparator',
  'mx-2 h-px bg-sidebar-border',
  'div',
)
